import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import logo from '../../assets/logos/quadroam-logo.svg';
import logoNegativo from '../../assets/logos/quadroam-negativo.svg';
import { CloseIcon, MenuIcon } from '../../components/icons';
import { useSession } from '../../lib/useSession';
import { ROLE_LABEL } from '../../features/members/roles';
import { consoleNav, visibleNav, type NavItem } from './nav';
import { BRAND } from '../../brand';

/** El panel lateral y el botón que lo abre se apuntan entre sí (`aria-controls`). */
const NAV_ID = 'console-nav';

const FOCUSABLE = 'a[href], button:not([disabled]), select, input';

export function ConsoleLayout({ nav = consoleNav, area = 'Consola de partners' }: { nav?: NavItem[]; area?: string }) {
  const { activeOrg, memberships, setActiveOrg, user, logout } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // En el teléfono el panel tapa el contenido: al cambiar de sección se cierra solo.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Mientras está abierto se comporta como una capa: Escape lo cierra y el foco no se escapa del panel.
  useEffect(() => {
    if (!menuOpen) return;
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active !== null && panel.contains(active);
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const email = user?.email ?? '';

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-mist">
      {menuOpen && (
        <div
          data-testid="fondo-menu"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-40 bg-asphalt/40 md:hidden"
        />
      )}

      <aside
        id={NAV_ID}
        ref={panelRef}
        className={`${
          menuOpen ? 'fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] shadow-2' : 'hidden'
        } shrink-0 overflow-y-auto border-r border-line bg-white p-3 md:static md:block md:w-60 md:max-w-none md:shadow-none`}
      >
        <div className="flex items-start justify-between gap-2 px-2 py-2">
          <div>
            <img src={logo} alt={BRAND} className="h-7 w-auto" />
            <p className="mt-1 text-xs text-ink-muted">{area}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              toggleRef.current?.focus();
            }}
            aria-label="Cerrar menú"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-gray-50 hover:text-ink md:hidden"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <nav aria-label={area} className="mt-4 flex flex-col gap-0.5">
          {visibleNav(nav, activeOrg?.role).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `rounded-md px-2 py-2.5 text-sm no-underline md:py-1.5 ${isActive ? 'bg-yellow-soft font-semibold text-ink' : 'text-ink hover:bg-gray-50'}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-asphalt px-4 py-2 text-chalk sm:px-6 sm:py-3">
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls={NAV_ID}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-asphalt-2 px-2 text-sm text-chalk hover:bg-asphalt-2 md:hidden"
          >
            <MenuIcon className="h-5 w-5" />
            Menú
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <img src={logoNegativo} alt="" aria-hidden="true" className="h-6 w-auto shrink-0" />
            {memberships.length > 1 ? (
              <label className="flex min-w-0 items-center gap-2 text-sm">
                <span className="sr-only">Organización activa</span>
                <select
                  className="max-w-[12rem] truncate rounded-sm border-0 bg-asphalt-2 px-2 py-1 text-chalk"
                  value={activeOrg?.orgId ?? ''}
                  onChange={(e) => setActiveOrg(e.target.value)}
                >
                  {memberships.map((m) => (
                    <option key={m.orgId} value={m.orgId}>
                      {m.orgName}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className="min-w-0 truncate text-sm">{activeOrg?.orgName ?? 'Sin organización'}</span>
            )}
            {activeOrg && (
              <span className="shrink-0 whitespace-nowrap rounded-full bg-yellow px-2 py-0.5 text-xs font-semibold text-ink">
                {ROLE_LABEL[activeOrg.role] ?? activeOrg.role}
              </span>
            )}
          </div>
          <div className="ml-auto flex min-w-0 items-center gap-2 text-sm">
            <span className="max-w-[10rem] truncate text-gray-300 sm:max-w-[18rem]" title={email}>
              {email}
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex min-h-10 shrink-0 items-center rounded-md px-2 font-semibold text-yellow hover:underline"
            >
              Salir
            </button>
          </div>
        </header>
        {/* El contenido tiene su propio scroll: la página nunca corre de lado y la barra superior queda fija. */}
        <main className="min-h-0 flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
